'use client'

import { motion } from 'motion/react'
import { SectionLabel } from './player-card'
import { useMk8Player } from '@/hooks/use-mk8-player'

const PREFIX_LABELS: Record<string, string> = {
  Gu: 'MK8',
  Du: 'DLC',
  G3ds: '3DS',
  Gwii: 'Wii',
  G64: 'N64',
  Gds: 'DS',
  Ggc: 'GCN',
  Gagb: 'GBA',
  Gsfc: 'SNES',
  Dgc: 'GCN',
  Dwii: 'Wii',
  Dsfc: 'SNES',
  Dagb: 'GBA',
  D3ds: '3DS',
  B3ds: '3DS',
  Bgc: 'GCN',
  Bsfc: 'SNES',
  Bu: 'Battle',
}

function humanizeToken(s = '') {
  return s
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizeCourseName(name: string) {
  if (!name) return 'Unknown Track'
  const m = /^([A-Za-z0-9]+)_(.+)$/.exec(name)
  if (!m) return humanizeToken(name)
  const prefix = m[1]
  const core = humanizeToken(m[2])
  const label = PREFIX_LABELS[prefix] || prefix
  return `${label} ${core}`
}

function mapAsset(name: string) {
  // Priority: direct game-id mapping (e.g. Gu_MarioCircuit.png)
  return `/assets/maps/${encodeURIComponent(name)}.png`
}

export function TrackBreakdown({ profileId }: { profileId?: string }) {
  const { summary } = useMk8Player(profileId)

  const maps = (summary?.maps || []).slice(0, 12)
  const totalRaces = maps.reduce((s: number, m: any) => s + Number(m.races || 0), 0)

  return (
    <section className="relative py-24 px-6" style={{ background: '#08080e' }}>
      <div
        className="absolute top-0 left-6 right-6 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(0,234,255,0.15), transparent)',
        }}
      />

      <div className="max-w-5xl mx-auto">
        <SectionLabel label="TRACK PERFORMANCE" />

        <div className="grid gap-3">
          <div
            className="grid grid-cols-12 gap-4 px-5 py-2"
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              fontSize: '10px',
              letterSpacing: '0.18em',
              color: 'rgba(255,255,255,0.25)',
            }}
          >
            <span className="col-span-5">TRACK</span>
            <span className="col-span-2 text-center">RACES</span>
            <span className="col-span-2 text-center">AVG PTS</span>
            <span className="col-span-3 text-right">PICK RATE</span>
          </div>

          {maps.map((track: any, i: number) => {
            const races = Number(track.races || 0)
            const avg = Number(track.avg || 0)
            const pickRate = totalRaces > 0 ? (races / totalRaces) * 100 : 0
            const color = pickRate >= 12 ? '#00ff88' : pickRate >= 7 ? '#00eaff' : '#a78bfa'

            return (
              <motion.div
                key={`${track.name}-${i}`}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{
                  delay: i * 0.06,
                  duration: 0.5,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ x: 4 }}
                className="grid grid-cols-12 gap-4 items-center px-5 py-4 rounded-xl transition-colors cursor-default"
                style={{
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                <div className="col-span-5 flex items-center gap-3">
                  <img
                    src={mapAsset(track.name)}
                    alt={normalizeCourseName(track.name)}
                    onError={(e: any) => {
                      e.currentTarget.src = '/assets/maps/fallback.svg'
                    }}
                    style={{
                      width: 58,
                      height: 32,
                      borderRadius: 6,
                      objectFit: 'cover',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                  />
                  <p
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontWeight: 700,
                      fontSize: '15px',
                      color: '#fff',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {normalizeCourseName(track.name)}
                  </p>
                </div>

                <span
                  className="col-span-2 text-center"
                  style={{
                    fontFamily: "'Russo One', sans-serif",
                    fontSize: '15px',
                    color: 'rgba(255,255,255,0.6)',
                  }}
                >
                  {races}
                </span>

                <span
                  className="col-span-2 text-center"
                  style={{
                    fontFamily: "'Russo One', sans-serif",
                    fontSize: '15px',
                    color: '#ffe600',
                  }}
                >
                  {avg.toFixed(2)}
                </span>

                <span
                  className="col-span-3 text-right"
                  style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    fontSize: '13px',
                    color,
                    letterSpacing: '0.06em',
                  }}
                >
                  {pickRate.toFixed(1)}%
                </span>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
